import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import type { TimelineEvent } from "../api/types";
import { workerTimelineDayQuery } from "./useWorkerTimelineDays";

export interface WorkerTimeline {
  events: TimelineEvent[];
  isLoading: boolean;
  isError: boolean;
}

/** Timeline de plusieurs workers sur quelques jours : une requête par (worker,
 * jour), sur la clé de useWorkerTimelineDays pour partager son cache. L'état est
 * porté par worker : un membre illisible ne rend pas les autres incertains.
 * Pas de rechargement au retour sur l'onglet : une équipe entière, c'est deux
 * requêtes par membre. */
export function useTeamTimelineDays(
  workerUrls: string[],
  dayKeys: string[],
): { byWorker: Map<string, WorkerTimeline>; isLoading: boolean } {
  const pairs = workerUrls.flatMap((w) => dayKeys.map((d) => [w, d] as const));
  const results = useQueries({
    queries: pairs.map(([w, d]) => ({
      ...workerTimelineDayQuery(w, d),
      refetchOnWindowFocus: false,
    })),
  });

  // La carte n'est reconstruite que si une requête a changé d'état ou de
  // données : l'écran qui la lit n'a pas à tout recalculer à chaque render.
  const signal = results
    .map((r, i) => `${pairs[i]?.join("|")}:${r.dataUpdatedAt}:${r.status}`)
    .join(",");
  const byWorker = useMemo(() => {
    const map = new Map<string, WorkerTimeline>();
    pairs.forEach(([w], i) => {
      const r = results[i];
      const cur = map.get(w) ?? {
        events: [],
        isLoading: false,
        isError: false,
      };
      map.set(w, {
        events: [...cur.events, ...(r?.data?.events ?? [])],
        isLoading: cur.isLoading || !!r?.isLoading,
        isError: cur.isError || !!r?.isError,
      });
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signal]);

  return { byWorker, isLoading: results.some((r) => r.isLoading) };
}
